import {
  useCanGoBackCallback,
  useHistory,
  useQueryParamsCallback,
} from '@aboutyou/router';
import {useCallback, useEffect, useRef, useSyncExternalStore} from 'react';

export interface LocationState {
  [key: string]: string;
}

interface ControlParam {
  urlSearchParam: string;
  allowedValues: string[];
}

interface Config {
  controlParams?: ControlParam[];
}

export const OPENED_STATE_KEY_PREFIX =
  '@aboutyou/ui-components::OPEN_STATE_MANAGER_';

export function createOpenedStateKey(key: string) {
  return `${OPENED_STATE_KEY_PREFIX}${key}`;
}

const DEFAULT_STATE = {} as LocationState;

export const useOpenedStateManager = (key: string, config?: Config) => {
  const history = useHistory();
  const getQueryParams = useQueryParamsCallback();
  const getCanGoBack = useCanGoBackCallback();
  const currentInternalProp = createOpenedStateKey(key);
  const internalPropNameRef = useRef(currentInternalProp);
  internalPropNameRef.current = currentInternalProp;

  const selector = useCallback(() => {
    return (history.location.state as LocationState)?.[
      internalPropNameRef.current
    ];
  }, [internalPropNameRef, history]);

  const value = useSyncExternalStore(history.listen, selector, () => undefined);
  const isOpen = Boolean(value);
  const isOpenRef = useRef(isOpen);
  isOpenRef.current = isOpen;
  const controlParams = useRef(config?.controlParams);
  controlParams.current = config?.controlParams;
  /**
   * *hide* does affect the history
   *  - if withNewHistoryEntry is set as true, a new history entry is pushed, else no new history entry but the current entry is modified (replaced)
   *  - use when an update is made and should be reflected in the history,
   *    e.g. opened modal is closed after making updates
   */
  const hide = useCallback(
    (config?: {withNewHistoryEntry?: boolean}) => {
      const updatedState = {
        ...(history.location?.state as LocationState),
      };

      delete updatedState[internalPropNameRef.current];
      let updatedSearch = history.location?.search;

      if (controlParams.current) {
        for (const {urlSearchParam} of controlParams.current) {
          const params = new URLSearchParams(updatedSearch);
          params.delete(urlSearchParam);
          updatedSearch = '?' + decodeURIComponent(params.toString());
        }
      }

      config?.withNewHistoryEntry
        ? history.pushUiUpdate({
            ...history.location,
            state: updatedState,
            search: updatedSearch,
          })
        : history.replaceUiUpdate({
            ...history.location,
            state: updatedState,
            search: updatedSearch,
          });
    },
    [history, internalPropNameRef, controlParams],
  );

  /**
   * *cancel* does NOT affect the history
   *  - no new history entry
   *  - history modification only on direct entry
   *  - use when a process is being cancelled, e.g. opened modal is closed WITHOUT updates
   */
  const cancel = useCallback(() => {
    if (getCanGoBack()) {
      history.goBack();
    } else {
      hide();
    }
  }, [history, getCanGoBack, hide]);

  /**
   * *submit* behaves like *hide* and replaces the current history entry, but it also forces the history to be treated as a push event and thus forcing a scroll to top
   *  - no new history entry but the current entry is modified (replaced)
   *  - use when an update is made and should be reflected in the history, and also should be scrolled to top
   *    e.g. filters flyout is submitted after selecting filters
   */
  const submit = useCallback(() => {
    const updatedState = {
      ...(history.location?.state as LocationState),
    };
    delete updatedState[internalPropNameRef.current];
    let updatedSearch = history.location?.search;

    if (controlParams.current) {
      for (const {urlSearchParam} of controlParams.current) {
        const params = new URLSearchParams(updatedSearch);
        params.delete(urlSearchParam);
        updatedSearch = '?' + decodeURIComponent(params.toString());
      }
    }

    history.replaceWithScrollToTop({
      ...history.location,
      state: updatedState,
      search: updatedSearch,
    });
  }, [history, internalPropNameRef, controlParams]);

  const open = useCallback(
    (options?: {replaceHistory?: boolean; parameter?: string}) => {
      const currentState = (history.location.state ||
        DEFAULT_STATE) as LocationState;
      if (
        isOpenRef.current &&
        (!options?.parameter ||
          // eslint-disable-next-line eqeqeq
          options?.parameter == currentState[internalPropNameRef.current])
      ) {
        return;
      }

      const updatedState = {
        ...(history.location?.state as LocationState),
      };
      updatedState[internalPropNameRef.current] = options?.parameter ?? '1';

      let updatedSearch = history.location?.search;
      if (controlParams.current) {
        for (const {urlSearchParam, allowedValues} of controlParams.current) {
          if (allowedValues.includes(options?.parameter ?? '')) {
            const params = new URLSearchParams(updatedSearch);
            params.set(urlSearchParam, options!.parameter!);
            updatedSearch = '?' + decodeURIComponent(params.toString());
          }
        }
      }

      if (options?.replaceHistory === true || isOpenRef.current) {
        history.replace({
          ...history.location,
          state: updatedState,
          search: updatedSearch,
        });
      } else {
        history.pushUiUpdate({
          ...history.location,
          state: updatedState,
          search: updatedSearch,
        });
      }
    },
    [history, internalPropNameRef, isOpenRef, controlParams],
  );

  const automaticallyOpen = useCallback(() => {
    if (!isOpenRef.current && controlParams.current) {
      for (const {urlSearchParam, allowedValues} of controlParams.current) {
        const localValue = getQueryParams().get(urlSearchParam);

        if (
          localValue &&
          allowedValues.includes(localValue) &&
          value !== localValue
        ) {
          open({parameter: localValue, replaceHistory: true});
          break;
        }
      }
    }
  }, [isOpenRef, open, value, controlParams]);

  useEffect(() => {
    // Check if we can auto open on control params change e.g. when they are
    // enabled after some value has loaded
    automaticallyOpen();
  }, [JSON.stringify(config?.controlParams)]);

  return {
    cancel,
    hide,
    submit,
    isOpen: isOpenRef.current,
    open,
    key,
    parameter: value,
  };
};
